import {
  Dispatch,
  SetStateAction,
  useContext,
  useMemo,
  useState,
  useEffect,
  useCallback,
} from 'react'
import { useIntl } from 'react-intl'
import { isEmpty } from 'lodash'

import { useAppDispatch, useAppSelector } from 'app/hooks'
import ConfirmModal from 'components/Modal/ConfirmModal'
import { userTopDomainsSelector } from 'features/auth/permissions/selectors'
import { RegularModerate } from 'components/Typography'
import { commonMessages } from 'app/i18n/messages'
import { JobInfoHeaderMenuMessages } from '../messages'
import { JobPageContext } from 'features/jobs/JobPageContext'
import { mapDomainsToDropdownOptions } from 'features/DomainsSelector/utils'
import DropDown, { DropdownOption } from 'components/Input/Dropdown'
import DomainSelector from './DomainSelector'
import { getDomainSubDomainsThunk } from 'components/DomainBreadcrumbs/slice'
import {
  domainToSubDomainsCacheSelector,
  selectedNavTopDomainIdSelector,
} from 'features/domains/selectors'
import { SpinnerCentered } from 'components/Loader'
import { DomainType } from 'features/domains/types'
import { DropdownWrapper } from '../../styles'
import { updateJobThunk } from 'features/jobs/slice'
import { fetchHomeJobThunk } from 'features/home/components/HomeJobInfo/slice'

interface MoveJobModalProps {
  setIsMoveJobOpen: Dispatch<SetStateAction<boolean>>
  setError: Dispatch<SetStateAction<object | undefined>>
}
function MoveJobModal({ setIsMoveJobOpen, setError }: MoveJobModalProps) {
  const dispatch = useAppDispatch()
  const { formatMessage } = useIntl()
  const [isLoading, setIsLoading] = useState(true)
  const [domainPath, setDomainPath] = useState<(DropdownOption | undefined)[]>([
    undefined,
  ])
  // const dispatch = useAppDispatch()
  const userTopDomains = useAppSelector(userTopDomainsSelector)
  const subDomainsCache = useAppSelector(domainToSubDomainsCacheSelector)
  const selectedNavTopDomainId = useAppSelector(selectedNavTopDomainIdSelector)
  const { jobName, jobId } = useContext(JobPageContext)

  const dropdownOptionsForTopDomains = useMemo(
    () => mapDomainsToDropdownOptions(userTopDomains || []),
    [userTopDomains]
  )

  const topSubDomainFromCache = subDomainsCache[selectedNavTopDomainId || '']

  useEffect(() => {
    async function getTopDomainSubDomains() {
      if (selectedNavTopDomainId && !topSubDomainFromCache) {
        await dispatch(
          getDomainSubDomainsThunk({ domainId: selectedNavTopDomainId })
        )
      }
      setIsLoading(false)
    }
    getTopDomainSubDomains()
  }, [selectedNavTopDomainId, dispatch, topSubDomainFromCache])

  const handleMoveJob = async () => {
    try {
      setIsLoading(true)
      const parentDomainId =
        (domainPath[domainPath.length - 1]?.value as string) ??
        (domainPath[domainPath.length - 2]?.value as string) ??
        undefined

      if (parentDomainId === undefined) {
        return
      }
      await dispatch(
        updateJobThunk({
          domainId: jobId,
          payload: {
            parentDomainId,
          },
        })
      )
      await dispatch(fetchHomeJobThunk(jobId, { configs: true }))
      setIsMoveJobOpen(false)
    } catch (err) {
      console.log('Error deleting job', err)
      setError(err)
      setIsMoveJobOpen(false)
    }
  }

  const handleRemove = useCallback(
    (depth: number) => {
      if (depth === 0) {
        setDomainPath([undefined])
        return
      }

      setDomainPath([...domainPath.slice(0, depth), undefined])
    },
    [setDomainPath, domainPath]
  )

  const getSubDomains = useCallback(
    (depth: number): DomainType[] => {
      if (domainPath.length) {
        let subDomains = subDomainsCache[domainPath[0]?.value as string] || []
        if (depth >= 1) {
          for (let i = 1; i < depth + 1; i++) {
            if (domainPath[i]) {
              subDomains =
                subDomains.find(
                  (d) => d.id === (domainPath[i]?.value as string)
                )?.subDomains || subDomains
            }
          }
        }

        return subDomains.filter((subdomain) => subdomain.job !== true)
      }
      return []
    },
    [domainPath, subDomainsCache]
  )

  const handleSelect = useCallback(
    (option: DropdownOption, depth: number) => {
      if (option === null) {
        handleRemove(depth)
        return
      }

      let newPath = domainPath.map((path, index) => {
        if (index === depth) {
          return option
        } else {
          return path
        }
      })

      if (depth + 1 < domainPath.length) {
        newPath = newPath.slice(0, depth + 1)
      }

      if (
        getSubDomains(depth - 1).find((d) => d.id === (option.value as string))
          ?.subDomains?.length
      ) {
        setDomainPath([...newPath, undefined])
      } else {
        setDomainPath(newPath)
      }
    },
    [domainPath, getSubDomains, handleRemove]
  )

  useEffect(() => {
    if (
      isEmpty(subDomainsCache) ||
      (!isEmpty(subDomainsCache) && domainPath[0])
    ) {
      return
    }

    if (
      subDomainsCache[dropdownOptionsForTopDomains[0]?.value as string]?.length
    ) {
      setDomainPath([dropdownOptionsForTopDomains[0], undefined])
    } else {
      setDomainPath([dropdownOptionsForTopDomains[0]])
    }
  }, [dropdownOptionsForTopDomains, subDomainsCache, domainPath])

  const subdomainSelectors = useCallback(() => {
    if (domainPath.length > 0) {
      return domainPath.slice(1).map((domain, i) => {
        // @ts-ignore
        const id = domainPath[i]?.value as string
        const options = mapDomainsToDropdownOptions(getSubDomains(i))
        if (options.length === 0) {
          return null
        }
        return (
          <DomainSelector
            handleSelect={(option) => handleSelect(option, i + 1)}
            key={id}
            id={id}
            value={domain as DropdownOption}
            options={options as DropdownOption[]}
          />
        )
      })
    }
  }, [domainPath, getSubDomains, handleSelect])

  return (
    <ConfirmModal
      isOpen
      onDiscard={() => setIsMoveJobOpen(false)}
      onConfirm={handleMoveJob}
      isConfirmBtnDisabled={isLoading}
      title={formatMessage(JobInfoHeaderMenuMessages.moveModalTitle, {
        jobName,
      })}
      discardBtnTitle={formatMessage(commonMessages.cancel)}
      confirmBtnTitle={formatMessage(JobInfoHeaderMenuMessages.moveModalSave)}
      variant="errorFilled"
    >
      {isLoading && <SpinnerCentered />}

      {!isLoading && (
        <>
          <RegularModerate>
            {formatMessage(JobInfoHeaderMenuMessages.selectLabel)}
          </RegularModerate>
          <DropdownWrapper>
            <DropDown
              name="topDomain"
              value={domainPath[0]}
              onChange={(option) => handleSelect(option, 0)}
              placeholder=""
              options={dropdownOptionsForTopDomains}
            />
            <>{subdomainSelectors()}</>
          </DropdownWrapper>
        </>
      )}
    </ConfirmModal>
  )
}

export default MoveJobModal
